import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  addCivilCaseFeedback,
  editCivilCaseResolution,
} from "services/civilCaseComplaintDetailService";
import { editCivilCaseComplaint } from "services/civilCaseComplaintService";
import { CivilCaseComplaint, CivilCaseResolutions, Roles } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  compliant: CivilCaseComplaint;
  resolution: CivilCaseResolutions;
  onClose: (refetch: boolean) => void;
};

const CivilCaseFeedbackForm: FC<Props> = ({
  compliant,
  resolution,
  onClose,
}) => {
  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const navigate = useNavigate();

  const [respondBackLoading, setRespondBackLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);

  const isManagement = hasRole(loginData?.roles, [Roles.management]);

  const [summery, setSummery] = useState("");

  const validateForm = () => {
    if (!summery) return "Summary is Required.";

    return "";
  };

  const handleSubmit = (
    event: React.FormEvent,
    feedbackType: "approve" | "respond_back",
  ) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    feedbackType === "approve" && setApproveLoading(true);
    feedbackType === "respond_back" && setRespondBackLoading(true);
    addCivilCaseFeedback({
      resolution_id: resolution?.id,
      given_by_role: "coordinator", // e.g., coordinator, director, deputy, general_attorney
      feedback_type: feedbackType,
      summary: summery,
      given_by_user_id: loginData?.user_id,
      resolution: resolution?.id,
    })
      .then(() => {
        if (feedbackType === "approve") {
          editCivilCaseComplaint({
            id: compliant?.id,
            status: "forwarded_to_registrar",
          });
        }
        editCivilCaseResolution({
          id: resolution?.id,
          s_escalated: false,
          decision_type: feedbackType === "approve" ? "resolved" : undefined,
        });
        showTooltip("Successfully Submitted!", "success");
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => {
        feedbackType === "approve" && setApproveLoading(false);
        feedbackType === "respond_back" && setRespondBackLoading(false);
        onClose(true);
      });
  };

  return (
    <div className="w-full bg-white rounded-xl">
      <div className="mb-6">
        {/* <h2 className="text-xl font-bold text-gray-900 mb-1">Org Unit</h2> */}
        <p className="text-sm text-gray-600">
          Add Feedback on Prosecutor Decision
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Summary *
          </label>
          <textarea
            rows={4}
            placeholder="Feedback summary..."
            value={summery}
            onChange={(e) => {
              setSummery(e.target.value);
            }}
            className="w-full border border-gray-300 rounded-lg bg-gray-50 p-3 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          type="button"
          className="flex items-center flex-1 justify-center gap-2 px-6 py-2.5 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={approveLoading || respondBackLoading}
          onClick={(e) => {
            e.preventDefault();
            setRespondBackLoading(true);
            handleSubmit(e, "respond_back");
          }}
        >
          {respondBackLoading ? (
            <>
              <span className="animate-spin">{LoadingIcon}</span>
              Processing...
            </>
          ) : (
            "Respond Back"
          )}{" "}
        </button>
        <button
          className="flex items-center flex-1 justify-center gap-2 px-6 py-2.5 bg-green-700 text-white font-medium rounded-lg hover:bg-green-800 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={approveLoading || respondBackLoading}
          onClick={(e) => {
            e.preventDefault();
            handleSubmit(e, "approve");
          }}
        >
          {approveLoading ? (
            <>
              <span className="animate-spin">{LoadingIcon}</span>
              Processing...
            </>
          ) : (
            "Approve"
          )}
        </button>
      </div>
    </div>
  );
};

export default CivilCaseFeedbackForm;
